import { localDb } from './database'
import { createEntityId, queueLocalEntity } from './mutations'
import { ensureLocalIdentity } from '@/services/identity/local-identity'
import type { Note } from '@/domain/types'
import { notebookTitle, notebookModule, notebookQuestionIds, notebookCover, validateNotebookContent, type NotebookInput } from '../../../shared/notebooks'

export async function listLocalNotebooks() {
  const identity = await ensureLocalIdentity()
  const books: Note[] = []
  await localDb.notes.where('category').equals('__mistake_notebook__').each(n => {
    if (n.workspaceId === identity.workspaceId && !n.deletedAtMs && n.type === 'mistake_notebook' && n.notebookBook?.schema === 1)
      books.push({ ...n, notebookInk: undefined, notebookQuestionInk: undefined, notebookLayout: undefined, notebookProgress: undefined })
  })
  return books.sort((a, b) => b.updatedAtMs - a.updatedAtMs || a.id.localeCompare(b.id))
}
export async function saveLocalNotebook(input: Partial<NotebookInput>, id?: string, expectedVersion?: number) {
  validateNotebookContent(input)
  const identity = await ensureLocalIdentity(), now = Date.now()
  return localDb.transaction('rw', localDb.notes, localDb.pendingOperations, localDb.appMetadata, async () => {
  const current = id ? await localDb.notes.get(id) : undefined
  if (id && (!current || current.workspaceId !== identity.workspaceId || current.deletedAtMs || !current.notebookBook)) throw new Error('错题本不存在')
  if (expectedVersion !== undefined && current?.version !== expectedVersion) throw new Error('错题本已更新，请刷新后重试')
  const title = notebookTitle(input.title ?? current?.title)
  const module = notebookModule(input.module ?? current?.notebookBook?.module)
  const ids = notebookQuestionIds(input.questionIds ?? current?.notebookBook?.questionIds)
  const questions = await localDb.notes.bulkGet(ids)
  if ((!current || input.questionIds) && questions.some(n => !n || n.workspaceId !== identity.workspaceId || n.deletedAtMs || n.type === 'mistake_notebook'
    || !(n.recordKind === 'question' || n.type === 'mistake' || n.category === '错题记录'))) throw new Error('所选题目不存在、已删除或不是错题')
  const cover = 'cover' in input ? notebookCover(input.cover) : current?.notebookBook?.cover
  const entityId = current?.id || createEntityId('book')
  const entity: Note = {
    ...(current || { id: entityId, workspaceId: identity.workspaceId, ownerUserId: identity.userId,
      createdAtMs: now, updatedAtMs: now, deletedAtMs: null, version: 1,
      type: 'mistake_notebook' as const, content: '', category: '__mistake_notebook__', tags: [], source: '',
      reviewStage: 0, reviewCount: 0, nextReviewAtMs: null, lastReviewedAtMs: null }),
    title, notebookModule: `__book__:${entityId}`, tags: [module], updatedAtMs: now,
    notebookBook: { ...current?.notebookBook, schema: 1, module, questionIds: ids, cover,
      sourceFile: input.sourceFile ?? current?.notebookBook?.sourceFile,showQuestionNotes:input.showQuestionNotes??current?.notebookBook?.showQuestionNotes },
    ...('notebookInk' in input ? { notebookInk: input.notebookInk } : {}),
    ...('notebookQuestionInk' in input ? { notebookQuestionInk: input.notebookQuestionInk } : {}),
    ...('notebookLayout' in input ? { notebookLayout: input.notebookLayout } : {}),
  }
  if (new TextEncoder().encode(JSON.stringify(entity)).length > 16 * 1024 * 1024) throw new Error('错题本笔迹过大，请减少选题范围后保存')
  await queueLocalEntity(localDb.notes, 'note', entity, current ? 'update' : 'create', current?.version ?? null, identity.deviceId)
  return entity
  })
}
/** Merge against the latest persisted note under the same write lock as the pending operation. */
export async function patchLocalNotebook(id: string, change: (current: Note) => Partial<Note>) {
  const identity = await ensureLocalIdentity()
  return localDb.transaction('rw', localDb.notes, localDb.pendingOperations, localDb.appMetadata, async () => {
    const current = await localDb.notes.get(id)
    if (!current || current.workspaceId !== identity.workspaceId || current.deletedAtMs || !current.notebookBook) throw new Error('错题本不存在或已删除')
    return await queueLocalEntity(localDb.notes, 'note', { ...current, ...change(current), updatedAtMs: Date.now() }, 'update', current.version, identity.deviceId)
  })
}
export async function deleteLocalNotebook(id: string, expectedVersion: number) {
  const identity = await ensureLocalIdentity(), current = await localDb.notes.get(id)
  if (!current || current.workspaceId !== identity.workspaceId || current.deletedAtMs || !current.notebookBook) throw new Error('错题本不存在')
  if (current.version !== expectedVersion) throw new Error('错题本已更新，请刷新后重试')
  const now = Date.now()
  await queueLocalEntity(localDb.notes, 'note', { ...current, deletedAtMs: now, updatedAtMs: now }, 'delete', current.version)
}
